/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// js/helper/currency.js
function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
  function defaultTo(value2, defaultValue) {
    return value2 == null || value2 !== value2 ? defaultValue : value2;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ",");
    decimal = defaultTo(decimal, ".");
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
    return dollarsAmount + centsAmount;
  }
  let value = "";
  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_decimals_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }
  if (formatString.indexOf("with_comma_separator") !== -1) {
    return formatString.replace(placeholderRegex, value);
  } else {
    return formatString.replace(placeholderRegex, value);
  }
}
let currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;

document.addEventListener('variant:changed', function(event) {
  let variant = event.detail.variant; // Gives you access to the whole variant details
  let form = event.target;
  if(variant.compare_at_price){
    $('.description_accordion-product-mrp_price').html(formatMoney(variant.compare_at_price, currencyFormat))
  }else{
    $('.description_accordion-product-mrp_price').html(formatMoney(variant.price, currencyFormat))
  }
});

$(document).ready(function() {
  function updateTotalDisplay(total_price,total_compare_price) {
    if (total_price === 0) {
      $(".product_family_twinning-meta-no-selection").show();
      $(".product_family_twinning-meta-count").hide();
      $(".product_family_twinning-meta__price-list-container").hide();
      $(".select-size-button-wrapperrr").show();
      $(".family_twinning-atc-button-wrapper").hide();
    } else {
      $(".product_family_twinning-meta-no-selection").hide();
      $(".product_family_twinning-meta-count").show();
      $(".select-size-button-wrapperrr").hide();
      $(".family_twinning-atc-button-wrapper").show();
      if(total_compare_price > total_price){
            $(".product_family_twinning-meta__price-list-container").show().html(
            `<div class="product-meta__price-list-container">
                <div class="price-list">
                    <span class="price price--highlight">${formatMoney(total_price, currencyFormat)}</span>
                    <span class="price price--compare">${formatMoney(total_compare_price, currencyFormat)}</span>
                </div>
            </div>`
            );
      }else{
        $(".product_family_twinning-meta__price-list-container").show().html(
            `<div class="product-meta__price-list-container">
                <div class="price-list">
                    <span class="price price--highlight">${formatMoney(total_price, currencyFormat)}</span>
                </div>
            </div>`
        );
      }
    }
  }

  function logTotalPrice(_this) {
    console.log('logTotalPrice')
    let total_price = 0;
    let total_compare_price = 0;
    $(_this).parents('.product__qv-info').find(".product-meta-ft_item-radio:checked:not(:disabled)").each(function(index) {
      let price = parseFloat($(this).attr("price"));
      let compare_price = parseFloat($(this).attr("compare_price"));
      if (!isNaN(price)) total_price += price;
      if (!isNaN(compare_price)) total_compare_price += compare_price;
    });
    $("#product_family_twinning-meta-count-num").text($(_this).parents('.product__qv-info').find(".product-meta-ft_item-radio:checked").length);
    console.log(total_price,total_compare_price);
    updateTotalDisplay(total_price,total_compare_price);
  }

  // This enables radio toggling off by label click
  $('body').on('click', '.product-meta-ft_item-title', function(e){
    console.log('clickkkkkk')
    var input = $("#" + $(this).attr("for"));
    if (input.prop("disabled")) return;

    if (input.prop("checked")) {
      // Prevent default behavior, uncheck, fire change event
      e.preventDefault();
      input.prop("checked", false).trigger("change");
      logTotalPrice(this);
    } else {
      input.prop("checked", true).trigger("change");
      // Normal radio selection
    }
  });

  $('.drawer_slideshow').slick({
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: false,
    arrows: false,
    dots: true
  });

  function updateSizeGuideRow() {
    console.log('ddddddddddddddddddd')
    // sab active remove
    $('.size-guide-modal-tab-content-item_table_body-row').removeClass('active');
    // checked input ka value lo
    $('.product-meta-ft_item-radio:checked').each(function () {
      const selectedValuee = $(this).attr('data-val');
      console.log('selectedValuee',selectedValuee);
      // matching row active karo
      $(`.size-guide-modal-tab-content-item_table_body-row[data-row="${selectedValuee}"]`).addClass('active');
    })
  }

  // Log price on every actual change
  $('body').on('change', '.product-meta-ft_item-radio', function(e){
    logTotalPrice(this);
    updateSizeGuideRow();
  });

    $('.family_twinning-atc-button').click(function(){
        // e.preventDefault();
        this.setAttribute('disabled', 'disabled');
        this.setAttribute('aria-busy', 'true');

        var _this = this;
        var items = [];
        var addSubProducts = true;
        var randomId = Math.random().toString(36).substring(2, 12);
        function addOnProducts() {
        var quantity = $('.product__info').find('input[name="quantity"]').val();
        $('.product__info').find('.product-meta-ft_item-radio:checked').each(function(i,e){
            items.push({
            quantity: quantity,
            id: $(this).attr('variant_id'),
            properties: {
                '_personalize': 'sub',
                '_type': 'add_on',
                '_bundleid': randomId
            }
            })
        });
        var data = {items};
        setTimeout(function(){
            $.ajax({
              type: 'POST',
              url: '/cart/add.js',
              async: false,
              data: {items},
              dataType: 'json',
              error: function(jqXHR, textStatus, errorThrown){
              var response = $.parseJSON(jqXHR.responseText);
              alert(response.description);
              },
              success: function() {
                  console.log('success');
                  document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                      bubbles: true
                  }));
                  document.getElementById('mini-cart').open = true;
                  $(_this).removeAttr('disabled');
                  $(_this).removeAttr('aria-busy');
              }
          });
        },1000);
        
        console.log('product',items,items[0].properties._bundleid);
        }
        if($('.product-meta-ft_item-radio:checked').length > 0){
            console.log('addOnProducts');
            addOnProducts();
        }
    });
});

$(document).ready(function(){

  $('.footer-content_item-header').click(function(){
    $(this).toggleClass('active');
    $(this).next('.footer-content_item-content').slideToggle();
  });

    $('body').on('click','.product-meta_offer-code-copy-box', function () {
        var _this = $(this);
        var text = $(this).attr('data-code');
        var tempInput = $("<input>");
        $("body").append(tempInput);
        tempInput.val(text).select();
        document.execCommand("copy");
        tempInput.remove();
        _this.addClass('active');
        setTimeout(function(){
            _this.removeClass('active');
        },2000);
    });

  $('body').on('click', '.sticky-form_select-size-button', function(e){
    e.preventDefault();
    const $target = $('.product-form__variants');
    if ($target.length) {
      if ($('body').hasClass('template--product')) {
        // Scroll smoothly to the section
        $('html, body').stop().animate({
          scrollTop: $target.offset().top - 200 // adjust offset as needed
        }, 600);
      }
      // Add shaking class
      $target.addClass('shaking');
      // Remove shaking after 2 seconds
      setTimeout(() => {
        $target.removeClass('shaking');
      }, 1200);
    }
  });

  $('body').on('click','.select-size-buttonn', function(){
    const $targett = $('.product-meta-custom-size_selection');
    if ($targett.length) {
      if ($('body').hasClass('template--product')) {
        $('html, body').stop().animate({
          scrollTop: $targett.offset().top - 200 // adjust offset as needed
        }, 600);
      }
      // Add shaking class
      $targett.addClass('shaking');
      // Remove shaking after 2 seconds
      setTimeout(() => {
        $targett.removeClass('shaking');
      }, 1200);
    }
  })

  $('body').on('click', '.sticky-form_select-size-button-wrap', function(e) {
    e.preventDefault();
    $('html').addClass('lock-all');
    $(this).closest('.product-sticky-from-bar').find('.sticky-form-overlay').addClass('active');
    $(this).closest('.product-sticky-from-bar').find('.sticky-form-close-btn').addClass('active');
    $(this).closest('.product-sticky-from-bar').find('.sticky-form-block-swatch-wrap').slideToggle();
  })

   $('body').on('click', '.sticky-form-overlay, .sticky-form-close-btn ', function(e) {    
    $(this).closest('.product-sticky-from-bar').find('.sticky-form-overlay').removeClass('active');
    $(this).closest('.product-sticky-from-bar').find('.sticky-form-close-btn').removeClass('active');
    $(this).closest('.product-sticky-from-bar').find('.sticky-form-block-swatch-wrap').slideToggle();
    $('html').removeClass('lock-all');
  })

  $('body').on('click', '.sticky-form_select-size-button-wrapp-desktop', function (e) {
    e.preventDefault();
    $('html').addClass('lock-all');
    $(this).closest('.product-sticky-form__inner').find('.sticky-form-block-swatch-wrap-desktop').attr('open','');
  })

  $('body').on('click', '.sticky_popup_button', function (e) {
    e.preventDefault();
    var $target = $(this).closest('.stick-form-swatch-inner').find('.item_options_wrapper');
    $target.addClass('shaking');
    // Remove shaking after 2 seconds
    setTimeout(() => {
      $target.removeClass('shaking');
    }, 1200);
  });

  $("body").on("click", ".sfbs-button-close, .sticky-form-block-swatch-overlay", function (){
    $('html').removeClass('lock-all');
    $(this).closest('.product-sticky-form__inner').find('.sticky-form-block-swatch-wrap-desktop').removeAttr('open', '');
  })


    $('.shop-by-collection_items-list').slick({
        centerMode: true,
        centerPadding: '0',
        slidesToShow: 3,
        slidesToScroll: 1,
        infinite: true,
        arrows: true,
        dots: true,
        responsive: [
            {
                breakpoint: 740,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,  
                }
            }
        ]
    });

    $('.favorite-character_items-list1').slick({
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: true,
        centerPadding: '0',
        infinite: false,
        arrows: true,
        dots: false,
        responsive: [
            {
                breakpoint: 740,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,  
                }
            }
        ]
    });

    $('.customer-speak_items-list').slick({
        slidesToShow: 3,
        slidesToScroll: 1,
        centerMode: true,
        centerPadding: '0',
        autoplay: true,
        infinite: true,
        arrows: true,
        dots: false,
        pauseOnFocus: false, 
        pauseOnHover: true,
        responsive: [
            {
                breakpoint: 740,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: false,
                    dots: true,  
                }
            }
        ]
    });

    // $('.favorite-character_items-list').slick({
    //     slidesToShow: 11,
    //     slidesToScroll: 1,
    //     autoplay: true,
    //     autoplaySpeed: 1,
    //     speed: 6000,
    //     dots: false,
    //     arrows: false,
    //     cssEase: 'linear',
    //     waitForAnimate: false,
    //     pauseOnFocus: true, 
    //     pauseOnHover: true,
    //     responsive: [
    //         {
    //             breakpoint: 740,
    //             settings: {
    //                 slidesToShow: 3,
    //                 slidesToScroll: 1,
    //             }
    //         }
    //     ]
    // });

  $('.hero-slider_items-list').slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      infinite: true,
      arrows: false,
      dots: true,
      autoplay: false,
      fade: true, 
      autoplaySpeed: 2000,
      speed: 600
  });

  $('.product-meta_offers-list').slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      infinite: true,
      arrows: true,
      dots: false,
  });

  $('body').on('click','.product-item-variant-item', function(){
    const items = [];
    const plp_variant = $(this).attr('data-variant-id');
    items.push({
      quantity: 1,
      id: plp_variant
      })
      var data = {items};
      $.ajax({
          type: 'POST',
          url: '/cart/add.js',
          async: false,
          data: {items},
          dataType: 'json',
          error: function(jqXHR, textStatus, errorThrown){
          var response = $.parseJSON(jqXHR.responseText);
          alert(response.description);
          },
          success: function() {
              console.log('success');
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                  bubbles: true
              }));
              document.getElementById('mini-cart').open = true;
          }
      });
  })

  $('body').on('click','.why-shop-from-us-video-button', function(){
    $(this).addClass('active');
    // $(this).closest('.why-shop-from-us-video').find('.pro-meta-video-cover-img').addClass('active');
    const video = $(this).closest('.why-shop-from-us-video').find('.why-shop-from-us-media_video')[0];
    video.play();
    video.controls = true;
 });

  $('.testimonial-sec-blocks').slick({
      slidesToShow: 4,
      slidesToScroll: 1,
      autoplay: false,
      speed: 600,
      dots: false,
      arrows: true,
      responsive: [
          {
              breakpoint: 1350,
              settings: {
                slidesToShow: 3,
               }
          },
          {
              breakpoint: 740,
              settings: {
                slidesToShow: 1,
                dots: true,
                arrows: false,
                infinite: false
               }
          }
      ]
  });

  if ($(window).width() <= 740) {
    if (!$('.image-with-text-items--list').hasClass('slick-initialized')) {
      $('.image-with-text-items--list').slick({
        slidesToShow: 1,
        dots: true,
        arrows: false,
        infinite: false
      });
    }
  } else {
    if ($('.image-with-text-items--list').hasClass('slick-initialized')) {
      $('.image-with-text-items--list').slick('unslick');
    }
  }

  $('body').on('click','.footer__item.footer__item--links .footer__item-title', function(){
    // $('.footer__item.footer__item--links .footer__item-title').removeClass('active');
    $(this).toggleClass('active');
    $(this).next().slideToggle();
  })

  // $('a[href^="/collections/"],a[href^="https://napchief.com/collections/"]').each(function(){
  //   var oldUrl = $(this).attr("href"); // Get current url
  //   // console.log(oldUrl,'oldUrl');
  //   if(oldUrl.indexOf('?') > -1){
  //     // var url = oldUrl+'&filter.v.availability=1'; 
  //   }else{
  //     var url = oldUrl+'?filter.v.availability=1';        
  //     var newUrl = oldUrl.replace(oldUrl, url); // Create new url
  //     // console.log('newUrl',newUrl);
  //     $(this).attr("href", newUrl); // Set herf value
  //   }         
  // });

  $('a[href*="/collections/"]').each(function () {
    let oldUrl = $(this).attr('href');

    if (!oldUrl.includes('filter.v.availability=1')) {
      let separator = oldUrl.includes('?') ? '&' : '?';

      $(this).attr(
        'href',
        oldUrl + separator + 'filter.v.availability=1'
      );
    }
  });

  const selectedVar = $('.product-form__option-selector .block-swatch input:checked');
  if (selectedVar.length) {
    const inputChecked = selectedVar.attr('data-val');
    $('.size-guide-modal-tab-content-item_table_body-row').removeClass('active');
    $(`.size-guide-modal-tab-content-item_table_body-row[data-row="${inputChecked}"]`).addClass('active');
  }

  // $('body').on('click', '.product-form__option-selector .block-swatch input[type="radio"] ', function(e){
  //   var $currentSelector = $(this).closest('.product-form__option-selector');
  //   var clicked = $currentSelector.attr('data-clicked') || 0;
  //   console.log(clicked);
  //   if(clicked == 0){
  //     const inputId = $(this).attr('id').replace('sticky','');
  //     console.log(inputId);
  //     const input = document.getElementById(inputId);
  
  //     if (input && !input.checked) {
  //       input.checked = true;
  //     }
      
  //     var masterselector = document.getElementById($(this).closest('product-variants').attr("form-id")).id;
  //     if($(this).closest('.drawer__content').length > 0){
  //       var productDataScript = $(this).closest('.drawer__content').find('script[type="application/json"][data-productjson]')[0];
  //     }else{
  //       var productDataScript = document.querySelector('script[type="application/json"][data-productjson]');  
  //     }
      
  //     if (productDataScript) {
  //       console.log(productDataScript);
  //       if($(this).closest('.drawer__content').length > 0){
  //         var productData = JSON.parse($(productDataScript).text());
  //       }else{        
  //         var productData = JSON.parse(productDataScript.textContent);
  //       }
          
  //       const variantId = parseInt($(this).attr('data-vid')); // Replace this with the actual variant ID you want to match
  //       const selectedVariant = productData.variants.filter(variant => {
  //           return variant.id === variantId; // Ensure data types match
  //       })?.[0];       
  //       $(this).closest('.product-form').find('[data-product-add-to-cart-button]').removeClass('selectasize');
  //       triggerEvent(masterselector, "variant:changed", { variant: selectedVariant });
        
  //       if($('body').hasClass('template--product')){
  //         const newUrl = new URL(window.location.href);
  //         if (selectedVariant.id) {
  //           newUrl.searchParams.set("variant", selectedVariant.id);
  //         } else {
  //           newUrl.searchParams.delete("variant");
  //         }
  //         window.history.replaceState({ path: newUrl.toString() }, "", newUrl.toString());  
  //       }
        
  //     } else {
  //         console.log("No JSON-LD product data found.");
  //     }       
  //       $currentSelector.attr('data-clicked', 1);      
  //   }          
  // });

});

$('body').on('click','.size-guide-modal-header-tab_item', function (e) {
  e.preventDefault();
  $(this).parents('.size-guide-modal_main').find('.size-guide-modal-header-tab_item').removeClass('active');
  $(this).addClass('active');
  $('.size-guide-modal-tab-content-item').hide();
  
  var activeTab = $(this).attr('href');
  $(this).parents('.size-guide-modal_main').find(activeTab).show();
  return false;
});

// Initialize original values as data attributes
// $(".size-guide-modal-tab-content-item_table_body-row").each(function () {
//   $(this).find(".size-guide-modal-tab-content-item_table_td--values").each(function () {
//     const inValue = parseFloat($(this).attr("data-in"));
//     if (!isNaN(inValue)) {
//       $(this).attr("data-cm", (inValue * 2.54).toFixed(0));
//     }
//   });
// });

// Handle unit conversion on click
  $('body').on('click', '.size-guide-modal-tab-content-converter_list-item', function() {    
    // Update active class
    $(this).parents('.size-guide-modal-content').find(".size-guide-modal-tab-content-converter_list-item").removeClass("active");
    $(this).addClass("active");

    // Determine the selected unit
    const unit = $(this).attr('data-href'); // CM or IN
    console.log("click",unit);

    // Update table values dynamically
    $(this).parents('.size-guide-modal-content').find(".size-guide-modal-tab-content-item_table_body-row").each(function () {
      $(this).find(".size-guide-modal-tab-content-item_table_td--values").each(function () {
        if (unit === "centimetre") {
          $(this).text($(this).data("cm")); // Show CM values
        } else {
          $(this).text($(this).data("in")); // Show IN values
        }
      });
    });
  });

// Store original values for reverting
// var originalData = [];
// $('.size-guide-modal-tab-content-item_table_body-row').each(function(i, row){
//   var $tds = $(row).find('.size-guide-modal-tab-content-item_table_td');
//   var rowData = [];
//   // store all columns text
//   $tds.each(function(index){
//       rowData.push($(this).text());
//   });
//   originalData.push(rowData);
// });

// Helper to convert a range (like "28-29") in inches to centimeters
// function convertRangeToCm(rangeStr) {
//   return rangeStr.split('-')
//       .map(function(x){ return (parseFloat(x) * 2.54).toFixed(1); })
//       .join('-');
// }

// $('body').on('click','.size-guide-modal-tab-content-converter_list-item', function (e) {
//   e.preventDefault();
//   var unit = $(this).attr('data-href');
//   $('.size-guide-modal-tab-content-converter_list-item').removeClass('active');
//   $(this).addClass('active');

//   $('.size-guide-modal-tab-content-item_table_body-row').each(function(i, row){
//       var $tds = $(row).find('.size-guide-modal-tab-content-item_table_td');

//       $tds.each(function(idx){
//           // skip first column (size column)
//           if(idx === 0) return;

//           var originalVal = originalData[i][idx];

//           if(unit === 'centimetre'){
//               // convert number or range to cm
//               $(this).text(convertRangeToCm(originalVal));
//           } else {
//               // revert to original
//               $(this).text(originalVal);
//           }
//       });
//   });
// });


// INFINITE COLLECTION
var pInfScrLoading = false;
var pInfScrDelay = 100;

function pInfScrExecute() {
  var pInfScrNode = $('.product-list').find('.next-page_link');

  // Only run if not already loading and next page exists
  if (!pInfScrLoading && pInfScrNode.length > 0) {
    var pInfScrURL = pInfScrNode.find('a').attr("href");
    $('.product-list').append('<img src="https://cdn.shopify.com/s/files/1/0092/3831/5068/files/transparent-loader.gif?v=1751462450" class="new-page-loading" />');

    $.ajax({
      type: 'GET',
      url: pInfScrURL,
      beforeSend: function() {
        pInfScrLoading = true;
        console.log('🔄 Loading next page...');
      },
      success: function(data) {
        // Remove "Next" link and old loader
        $('.product-list').find('.next-page_link').remove();
        $('.product-list').find('.new-page-loading').remove();

        // Extract new products and append
        var filteredData = $(data).find(".product-list__inner").html();
        $('.product-list__inner').append(filteredData);

        pInfScrLoading = false;
        console.log('✅ New products loaded.');
      },
      error: function() {
        console.warn('⚠️ Failed to load next page.');
        pInfScrLoading = false;
      },
      dataType: "html"
    });
  }
}

// SCROLL TRIGGER
$(window).on('scroll', function() {
  var $list = $('.product-list');
  if ($list.length === 0 || pInfScrLoading) return;

  // Check if user is near the bottom of the product list
  var listBottom = $list.offset().top + $list.outerHeight();
  var scrollBottom = $(window).scrollTop() + $(window).height();

  if (scrollBottom + 100 >= listBottom) { // trigger 100px before end
    pInfScrExecute();
  }
});



// Set the date we're counting down to
var getDate = $('.timer_main').attr('data-time');
var countDownDate = new Date(getDate).getTime();

// Update the count down every 1 second
var x = setInterval(function () {
    // Get today's date and time
    var now = new Date().getTime();

    // Find the distance between now and the count down date
    var distance = countDownDate - now;

    // Time calculations for days, hours, minutes and seconds
    var days = Math.floor(distance / (1000 * 60 * 60 * 24));
    var hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    var minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    var seconds = Math.floor((distance % (1000 * 60)) / 1000);

    var days_content = '',
    hours_content = '',
    minutes_content = '',
    seconds_content = '';
    if (days > 0) {
    days_content = `<div class="timer_item"><span class="timer_item-val"><span class="numval">${String(days).padStart(2,'0')}</span>D</span><span class="timer_item-text">:</span></div>`;
    }
    if (hours > 0) {
    hours_content = `<div class="timer_item"><span class="timer_item-val"><span class="numval">${String(hours).padStart(2,'0')}</span>H</span><span class="timer_item-text">:</span></div>`;
    }
    minutes_content = `<div class="timer_item"><span class="timer_item-val"><span class="numval">${String(minutes).padStart(2,'0')}</span>M</span><span class="timer_item-text">:</span></div>`;
    seconds_content = `<div class="timer_item"><span class="timer_item-val"><span class="numval">${String(seconds).padStart(2,'0')}</span>S</span><span class="timer_item-text"></span></div>`;
    // Display the result in the element with id="demo"
    $('.timer_main:not(.productinfo_timerwp)').each(function (index) {
    $(this).html(`
        ${days_content}
        ${hours_content}
        ${minutes_content}
        ${seconds_content}
        `);
    });

    var pdpdays_content = '',
    pdphours_content = '',
    pdpminutes_content = '',
    pdpseconds_content = '';
    if (days > 0) {
    pdpdays_content = `<div class="timer_item"><span class="timer_item-val"><span class="numval">${String(days).padStart(2,'0')}</span>Days</span></div>`;
    }
    if (hours > 0) {
    pdphours_content = `<div class="timer_item"><span class="timer_item-val"><span class="numval">${String(hours).padStart(2,'0')}</span>Hours</span></div>`;
    }
    pdpminutes_content = `<div class="timer_item"><span class="timer_item-val"><span class="numval">${String(minutes).padStart(2,'0')}</span>Minutes</span></div>`;
    pdpseconds_content = `<div class="timer_item"><span class="timer_item-val"><span class="numval">${String(seconds).padStart(2,'0')}</span>Seconds</span></div>`;
    
    $('.timer_main.productinfo_timerwp').each(function (index) {
    $(this).html(`
        ${pdpdays_content}
        ${pdphours_content}
        ${pdpminutes_content}
        ${pdpseconds_content}
        `);
    });

    // If the count down is finished, write some text
    if (distance < 0) {
    clearInterval(x);
    $('.timer_main-wrapper').hide();
    }
}, 1000);


// ADD IN custom.js
$(function() {

  // exit completely if not mobile
  if (window.innerWidth > 740) return;
  
  var imgTapStartX = 0;
  var imgTapStartY = 0;
  var imgIsDragging = false;

  $(document).on('touchstart', '.product__media-list-wrapper', function(e) {
    imgTapStartX = e.originalEvent.touches[0].clientX;
    imgTapStartY = e.originalEvent.touches[0].clientY;
    imgIsDragging = false;
  });

  $(document).on('touchmove', '.product__media-list-wrapper', function(e) {
    var diffX = Math.abs(e.originalEvent.touches[0].clientX - imgTapStartX);
    var diffY = Math.abs(e.originalEvent.touches[0].clientY - imgTapStartY);
    if (diffX > 8 || diffY > 8) {
      imgIsDragging = true;
    }
  });

  $(document).on('touchend', '.product__media-list-wrapper', function(e) {
    if (imgIsDragging) {
      imgIsDragging = false;
      return;
    }
    // mobile only
    if (window.innerWidth > 740) return;
    
    var zoomBtn = $('.product__zoom-button');
    if (zoomBtn.length) {
      zoomBtn[0].click();
    }
  });
});